import { agentModeAtomFamily } from "@/components/ai/chat/state";
import { AgentMode } from "@/components/ai/prompt-input/agent-mode";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useSetAtom } from "jotai";
import { formatClarifyingQuestionsResponse } from "@/components/ai/clarifying-questions-utils";
import { formatError } from "@/utils/errors";
import { type FC, useState, useEffect, useCallback, useRef } from "react";
import { FragmentClarifyingQuestions } from "@/components/ai/FragmentClarifyingQuestions";
import { FragmentNextStepsProposal } from "@/components/ai/FragmentNextStepsProposal";
import type { PendingInteractiveFragment } from "@/components/ai/pending-interactive-utils";

type PendingInteractiveBlockProps = {
    fragment: PendingInteractiveFragment | null;
    onSendMessage?: (message: string) => void | Promise<void>;
    environmentId?: string;
    className?: string;
};

export const PendingInteractiveBlock: FC<PendingInteractiveBlockProps> = ({
    fragment,
    onSendMessage,
    environmentId,
    className,
}) => {
    const setAgentMode = useSetAtom(agentModeAtomFamily(environmentId ?? ""));
    const { toast } = useToast();
    const [actionTaken, setActionTaken] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    const [unmounted, setUnmounted] = useState(false);

    // Track which fragment ID the actionTaken state belongs to.
    // This prevents a stale actionTaken=true (set asynchronously for a
    // previous fragment) from triggering the dismiss animation on a new
    // fragment that arrives in the same render cycle.
    const actionTakenForRef = useRef<string | undefined>(undefined);

    const setActionTakenForFragment = useCallback(
        (value: boolean) => {
            if (value) {
                actionTakenForRef.current = fragment?.id;
            }
            setActionTaken(value);
        },
        [fragment?.id],
    );

    useEffect(() => {
        actionTakenForRef.current = undefined;
        setActionTaken(false);
        setDismissing(false);
        setUnmounted(false);
    }, [fragment?.id]);

    // Start exit animation when the last question is answered, but only
    // if actionTaken was set for the current fragment — not a stale one.
    useEffect(() => {
        if (!actionTaken || fragment?.type !== "clarifyingQuestions") return;
        if (actionTakenForRef.current !== fragment?.id) return;
        setDismissing(true);
    }, [actionTaken, fragment?.type, fragment?.id]);

    const handleAnimationEnd = useCallback(() => {
        if (dismissing) {
            setUnmounted(true);
        }
    }, [dismissing]);

    if (!fragment || unmounted) return null;

    // For non-clarifyingQuestions, keep instant dismiss
    if (actionTaken && fragment.type !== "clarifyingQuestions") return null;

    const handleSendMessage = async (message: string) => {
        if (!onSendMessage) {
            toast({
                title: "Unable to send response",
                description: "Please reload the page and try again.",
            });
            return;
        }

        try {
            await onSendMessage(message);
            setActionTakenForFragment(true);
            // Switch to Agent mode only after the message was sent successfully.
            if (fragment.type === "nextStepsProposal" && environmentId) {
                setAgentMode(AgentMode.Agent);
            }
        } catch (error) {
            toast({
                title: "Failed to send response",
                description: formatError(error),
            });
        }
    };

    if (fragment.type === "clarifyingQuestions") {
        return (
            <div
                key={fragment.id}
                className={cn(
                    "mx-1 -mb-2 mt-1 w-auto",
                    dismissing
                        ? "duration-[400ms] ease-in-out animate-out fade-out slide-out-to-bottom-4 fill-mode-forwards"
                        : "duration-[400ms] ease-in-out animate-in fade-in slide-in-from-bottom-4",
                    className,
                )}
                onAnimationEnd={handleAnimationEnd}
            >
                <FragmentClarifyingQuestions
                    fragment={fragment}
                    onSubmit={(answers) => {
                        const response = formatClarifyingQuestionsResponse(fragment.questions, answers);
                        void handleSendMessage(response);
                    }}
                />
            </div>
        );
    }

    return (
        <div
            key={fragment.id}
            className={cn("mx-1 -mb-2 mt-1 w-auto duration-200 animate-in fade-in slide-in-from-bottom-2", className)}
        >
            <FragmentNextStepsProposal fragment={fragment} onActionClick={handleSendMessage} />
        </div>
    );
};
